/**
 * FO-307-A1 - de sync-engine: speelt de wachtrij af zodra er verbinding is.
 *
 * Volgorde is strikt FIFO (uren vóór handtekening). Op de eerste netwerkfout
 * stoppen we (we zijn kennelijk weer offline) i.p.v. de rest te blijven proberen.
 * Conflict (409) en definitieve fout (4xx) blijven als item bewaard zodat niets
 * stil vervalt en de monteur een melding ziet.
 */
import { sortMutationsForSync, applyOutcome, type SyncOutcome } from '@streep/utils'
import { listMutations, saveMutation, removeMutation } from './queue'
import { dispatchMutation } from './dispatcher'
import type { QueuedMutation } from './types'

export interface SyncSummary {
  synced: number
  conflicts: number
  failed: number
  remaining: number
  stoppedOffline: boolean
}

let running = false

/**
 * Verwerkt de wachtrij één keer. Herentree-veilig: een tweede gelijktijdige
 * aanroep is een no-op (voorkomt dubbele verzending van hetzelfde item).
 */
export async function processQueue(): Promise<SyncSummary> {
  if (running) {
    return { synced: 0, conflicts: 0, failed: 0, remaining: await pendingCount(), stoppedOffline: false }
  }
  running = true
  let synced = 0
  let conflicts = 0
  let failed = 0
  let stoppedOffline = false
  try {
    const all = await listMutations()
    const pending = sortMutationsForSync(all.filter((m) => m.status === 'pending' || m.status === 'syncing'))

    for (const item of pending) {
      // Markeer als 'syncing' zodat de UI het live ziet.
      await saveMutation({ ...item, status: 'syncing' })
      const { outcome, error } = await dispatchMutation(item)
      const update = applyOutcome<QueuedMutation>(item, outcome, error)
      if (update.action === 'remove') {
        await removeMutation(update.id)
        synced++
      } else {
        await saveMutation(update.mutation)
        if (update.mutation.status === 'conflict') conflicts++
        else if (update.mutation.status === 'failed') failed++
      }
      if (isNetworkError(outcome)) {
        stoppedOffline = true
        break
      }
    }
  } finally {
    running = false
  }
  return { synced, conflicts, failed, remaining: await pendingCount(), stoppedOffline }
}

function isNetworkError(o: SyncOutcome): boolean {
  return o.kind === 'network-error'
}

export async function pendingCount(): Promise<number> {
  const all = await listMutations()
  return all.filter((m) => m.status === 'pending' || m.status === 'syncing').length
}
